import React, { useState } from 'react';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { HiOutlineBell } from 'react-icons/hi';
import { MdLink } from 'react-icons/md';
import moment from 'moment';
import { useNotifications } from '../../context/NotificationsContext';

const SEVERITY_ALERT = 1;
const ALERT_COLOR = '#dc3545';
const INFO_COLOR = '#0d6efd';
const UNREAD_BACKGROUND = '#EAF2FB';

// DB timestamps are UTC wall-clock strings with no timezone marker
const dbMoment = (value) => moment.utc(value).local();

const isAlert = (notification) => parseInt(notification.severity) === SEVERITY_ALERT;
const isUnread = (notification) => !parseInt(notification.is_read);

// links is a JSON-encoded string; only http/https urls are rendered
const parseLinks = (notification) => {
  try {
    const parsed = typeof notification.links === 'string'
      ? JSON.parse(notification.links)
      : notification.links;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(link => link && /^https?:\/\/.+/i.test(link.url || ''));
  } catch (error) {
    return [];
  }
};

const severityDot = (notification, size = 8) => (
  <span style={{
    display: 'inline-block',
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    flexShrink: 0,
    backgroundColor: isAlert(notification) ? ALERT_COLOR : INFO_COLOR,
  }}/>
);

const BellToggle = React.forwardRef(({ children, onClick }, ref) => (
  <a
    href=""
    ref={ref}
    onClick={(e) => { e.preventDefault(); onClick(e); }}
    style={{ color: 'black', position: 'relative', display: 'inline-flex', alignItems: 'center' }}
  >
    {children}
  </a>
));

const NotificationsBell = () => {
  const { notifications, unreadCount, markRead } = useNotifications();
  const [selectedNotification, setSelectedNotification] = useState(null);

  const openNotification = (notification) => {
    markRead(notification.uuid);
    setSelectedNotification(notification);
  };

  const renderItem = (notification) => {
    const unread = isUnread(notification);
    return (
      <Dropdown.Item
        as="div"
        key={notification.uuid}
        onClick={() => openNotification(notification)}
        style={{
          padding: '10px 14px',
          cursor: 'pointer',
          borderBottom: '1px solid #eee',
          backgroundColor: unread ? UNREAD_BACKGROUND : '#fff',
          whiteSpace: 'normal',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {severityDot(notification)}
          <span style={{
            flex: 1,
            marginLeft: '8px',
            fontSize: '13px',
            fontWeight: unread ? 600 : 400,
            color: '#333',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {notification.title}
          </span>
          <span style={{ fontSize: '11px', color: '#999', marginLeft: '8px', whiteSpace: 'nowrap' }}>
            {dbMoment(notification.created_at).fromNow(true)}
          </span>
        </div>
        {!!notification.body && (
          <div style={{
            fontSize: '12px',
            color: '#757575',
            marginTop: '2px',
            marginLeft: '16px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {notification.body}
          </div>
        )}
      </Dropdown.Item>
    );
  };

  const renderDetailModal = () => {
    const notification = selectedNotification;
    if (!notification) return null;
    const links = parseLinks(notification);
    return (
      <Modal show={true} onHide={() => setSelectedNotification(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '17px' }}>
            <span style={{ marginRight: '8px' }}>{severityDot(notification, 10)}</span>
            {notification.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-row justify-content-between mb-2">
            <span style={{ fontSize: '12px', fontWeight: 700, color: isAlert(notification) ? ALERT_COLOR : INFO_COLOR }}>
              {isAlert(notification) ? 'ALERT' : 'INFO'}
            </span>
            <span style={{ fontSize: '12px', color: '#999' }}>
              {dbMoment(notification.created_at).format('MMM D, YYYY h:mm A')}
            </span>
          </div>
          {notification.body ? (
            <p style={{ whiteSpace: 'pre-wrap', marginBottom: links.length > 0 ? '12px' : 0 }}>
              {notification.body}
            </p>
          ) : (
            <p className="text-muted" style={{ marginBottom: links.length > 0 ? '12px' : 0 }}>
              No message body.
            </p>
          )}
          {links.map((link, index) => (
            <div key={index} style={{ padding: '6px 0', borderTop: index === 0 ? '1px solid #eee' : 'none' }}>
              <MdLink style={{ verticalAlign: '-2px', marginRight: '6px' }}/>
              <a href={link.url} target="_blank" rel="noopener noreferrer">
                {link.label || link.url}
              </a>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setSelectedNotification(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <>
      <Dropdown align="end">
        <Dropdown.Toggle as={BellToggle} id="notifications-bell">
          <HiOutlineBell style={{ fontSize: 26 }}/>
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute',
              top: '-3px',
              right: '-7px',
              backgroundColor: ALERT_COLOR,
              color: '#fff',
              fontSize: '10px',
              fontWeight: 600,
              borderRadius: '999px',
              padding: '1px 5px',
              lineHeight: '13px',
            }}>
              {unreadCount}
            </span>
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ width: '360px', padding: 0, maxHeight: '440px', overflowY: 'auto' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 14px',
            borderBottom: '1px solid #ddd',
          }}>
            <span style={{ fontSize: '14px', fontWeight: 600 }}>Notifications</span>
            <span style={{ fontSize: '12px', color: '#757575' }}>
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            </span>
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: '24px 14px', textAlign: 'center', fontSize: '13px', color: '#999' }}>
              No notifications yet. Updates and alerts from DST will appear here.
            </div>
          ) : (
            notifications.map(renderItem)
          )}
        </Dropdown.Menu>
      </Dropdown>
      {renderDetailModal()}
    </>
  );
};

export default NotificationsBell;
